import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import agent from "../../../agent";
import { buildPreviewSummary, buildCommitSummary } from "../CarrierTariffs/importAdapters";
import ProbeTable from "../CarrierTariffs/ProbeTable";
import { mileLabel } from "./mileTypes";

/**
 * Заливка файла в тарифную карту.
 *
 * Шаги те же, что и у заливки тарифа перевозчика: посмотреть, что нашлось в
 * файле, и только потом подтвердить. Отличие одно и важное — заливка замещает
 * содержимое ЭТОЙ карты и не трогает ни стандартный тариф, ни другие карты.
 *
 * Строки файла должны быть того же плеча, что и карта: карту первой мили,
 * набитую магистралью, расчёт просто не увидит. Проверяет это калькулятор,
 * здесь только показывается сообщение об отказе.
 */
const PlanImportDialog = ({ visible, onHide, plan, onDone }) => {
  const [intracityOnly, setIntracityOnly] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const [stage, setStage] = React.useState("idle");
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!visible) return;
    // Внутригородская карта приходит файлом без магистрали — отдельный разбор.
    setIntracityOnly(plan?.mileType === "INTRACITY");
    setFile(null);
    setPreview(null);
    setReport(null);
    setError(null);
    setStage("idle");
  }, [visible, plan]);

  const reset = () => {
    setPreview(null);
    setReport(null);
    setError(null);
    setStage("idle");
  };

  const runPreview = () => {
    setStage("previewing");
    setError(null);
    agent.TariffPlans.importPreview(file, intracityOnly)
      .then((response) => {
        setPreview(response);
        setStage("preview");
      })
      .catch((e) => {
        setError(readError(e, "Не удалось разобрать файл"));
        setStage("idle");
      });
  };

  const runCommit = () => {
    setStage("committing");
    setError(null);
    agent.TariffPlans.importCommit({
      planId: plan.id,
      file,
      intracityOnly,
      fingerprint: preview.fingerprint,
    })
      .then((response) => {
        setReport(response.report);
        setStage("done");
        onDone?.();
      })
      .catch((e) => {
        setError(readError(e, "Заливка не выполнена"));
        setStage("preview");
      });
  };

  const summary = buildPreviewSummary(preview);
  const commitSummary = buildCommitSummary(report);
  const canPreview = Boolean(file) && stage !== "previewing";
  const canCommit = Boolean(preview?.canCommit) && stage === "preview";

  return (
    <Dialog header={plan ? `Заливка в карту «${plan.name}» (${mileLabel(plan.mileType)})` : "Заливка карты"}
            visible={visible} style={{ width: "900px" }} modal onHide={onHide}
            footer={<Button label="Закрыть" icon="pi pi-times" className="p-button-text" onClick={onHide} />}>

      <Message severity="warn" style={{ display: "block", marginBottom: 16 }}
               text="Заливка замещает содержимое этой карты целиком. Стандартный тариф и другие карты не затрагиваются." />
      <Message severity="info" style={{ display: "block", marginBottom: 16 }}
               text="Перевозчик и услуга у карты не выбираются: цена из карты действует на направлении независимо от того, кто повезёт. Сроки доставки остаются перевозчика." />

      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Checkbox inputId="plan-intracity-only" checked={intracityOnly}
                    onChange={(e) => { setIntracityOnly(e.checked); reset(); }} />
          <span>Файл только с внутригородом</span>
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Файл карты (.xlsx)</span>
          <input type="file" accept=".xlsx" aria-label="Файл карты"
                 onChange={(e) => { setFile(e.target.files?.[0] ?? null); reset(); }} />
        </label>

        <div>
          <Button label="Посмотреть содержимое" disabled={!canPreview} onClick={runPreview} />
        </div>
      </div>

      {error && <Message severity="error" text={error} style={{ display: "block", marginBottom: 16 }} />}

      {(stage === "previewing" || stage === "committing") && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}

      {preview && stage !== "done" && (
        <section style={{ marginBottom: 24 }}>
          <h3>Что нашлось в файле</h3>
          <div style={tileRow}>
            <Tile label="Точек лестницы" value={summary.ladderPoints} />
            <Tile label="Маршрутов" value={summary.routes} />
            <Tile label="Листов" value={summary.sheets} />
            <Tile label="Закрытых направлений" value={blockedRoutes(preview)}
                  tone={blockedRoutes(preview) ? "warn" : "ok"} />
            <Tile label="Ошибок" value={summary.errors} tone={summary.errors ? "error" : "ok"} />
          </div>

          {preview.sheets?.length > 0 && (
            <ul style={{ marginTop: 12 }}>
              {preview.sheets.map((sheet) => (
                <li key={sheet.sheetName}>
                  <strong>{sheet.sheetName}</strong> ({mileLabel(sheet.mileType)}): строк{" "}
                  {sheet.rowsAccepted} из {sheet.rowsRead}, маршрутов {sheet.routes}, точек лестницы{" "}
                  {sheet.ladderPoints}
                  {sheet.blockedRoutes > 0 && `, закрытых направлений ${sheet.blockedRoutes}`}
                </li>
              ))}
            </ul>
          )}

          {blockedRoutes(preview) > 0 && (
            <Message severity="info" style={{ display: "block", marginTop: 12 }}
                     text="Направления с пометкой «блокировать» закроются для партнёров этой карты. Цены таких строк всё равно заливаются: снимете блокировку — вектор сразу посчитается по карте." />
          )}

          {!preview.canCommit && (
            <Message severity="error" style={{ display: "block", marginTop: 12 }}
                     text="Заливка невозможна: в файле есть ошибки разбора." />
          )}

          <div style={{ marginTop: 16 }}>
            <Button label="Залить в карту" severity="danger" disabled={!canCommit} onClick={runCommit} />
          </div>
        </section>
      )}

      {report && stage === "done" && (
        <section>
          <h3>Карта залита</h3>
          <div style={tileRow}>
            <Tile label="Строк принято" value={commitSummary.rowsApplied} />
            <Tile label="Ног" value={commitSummary.legsCreated} />
            <Tile label="Диапазонов" value={commitSummary.weightRangesCreated} />
            <Tile label="Закрытых направлений" value={report.blockedDirections ?? 0}
                  tone={report.blockedDirections ? "warn" : "ok"} />
            <Tile label="Цена падает" value={commitSummary.monotonicityViolations}
                  tone={commitSummary.monotonicityViolations ? "error" : "ok"} />
          </div>

          {commitSummary.monotonicityViolations > 0 && (
            <Message severity="error" style={{ display: "block", marginTop: 12 }}
                     text="На части весов цена падает при росте веса. Это дефект расчёта — сообщите разработчикам." />
          )}

          <h3 style={{ marginTop: 24 }}>Контрольные точки</h3>
          <ProbeTable probe={report.probe} />
        </section>
      )}
    </Dialog>
  );
};

const blockedRoutes = (preview) =>
  (preview?.sheets ?? []).reduce((sum, sheet) => sum + (sheet.blockedRoutes ?? 0), 0);

const Tile = ({ label, value, tone = "default" }) => {
  const palette = {
    default: { background: "#eff6ff", border: "#bfdbfe" },
    ok: { background: "#ecfdf5", border: "#a7f3d0" },
    warn: { background: "#fffbeb", border: "#fde68a" },
    error: { background: "#fef2f2", border: "#fecaca" },
  }[tone];

  return (
    <div style={{ padding: "10px 12px", borderRadius: 8, background: palette.background, border: `1px solid ${palette.border}` }}>
      <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: "1.35rem", fontWeight: 600 }}>{value}</div>
    </div>
  );
};

const tileRow = { display: "flex", flexWrap: "wrap", gap: 8 };

const readError = (e, fallback) =>
  e?.response?.body?.messages?.[0] || e?.response?.body?.message || e?.response?.body?.error
  || e?.message || fallback;

export default PlanImportDialog;
